// prototypes/AllMetricsTable.tsx (v2.19)
// 「予算・前年比較」 タブ 3段目: 行 = 20 指標 / 列 = 月次 + 月平均 + 対前年・対予算。
//   - セル値: 当月実績
//   - セル色: トグルで 前年比 OR 予算比 を切替 (5 階調 / direction を尊重)
//   - 右側固定列: 月平均 / 月平均(前年) / 月平均(予算) / 対前年 / 対予算
//   - 列順 = budgetSpec.ts 配列順 (cards/charts と同じ + 表のみ 薬剤材料費 / 病床利用率)
// data は親 PrototypePanel から ResolvedMetric[] で渡される (派生指標解決済み)。
import { useMemo, useState } from "react";
import { fmtByUnitValueOnly, isYenCardMetric, yenUseOku } from "../../lib/format";
import { useFilters } from "../../store";
import type { Direction } from "@shared/api";
import { movingAvgTrend, type ResolvedMetric } from "./budgetSpec";

interface Props { resolved: ResolvedMetric[]; targetName: string; avgMode?: boolean; }

type ColorMode = "yoy" | "budget";

function cellBg(pct: number | null, direction: Direction): string {
  if (pct == null || direction === "neutral") return "";
  const p = direction === "lower_is_better" ? -pct : pct;
  if (p >= 0.10) return "bg-emerald-300";
  if (p >= 0.05) return "bg-emerald-100";
  if (p >= -0.05) return "";
  if (p >= -0.10) return "bg-red-100";
  return "bg-red-300";
}

// 月末未確定で 0 になる指標 (Cards/Charts と同じ判定 — 派生 薬剤材料費 / GP単価_在院 を含む)
const SKIP_ZERO_KEYS = new Set([
  "GP", "GP単価", "GP単価_在院",
  "出来高換算薬剤費", "出来高換算材料費", "出来高換算薬剤材料費",
]);

function monthLabel(yyyymmdd: string): string {
  const [y, m] = yyyymmdd.split("-");
  const mi = parseInt(m, 10);
  return mi === 1 || mi === 4 ? `${y.slice(2)}/${mi}月` : `${mi}月`;
}

export function AllMetricsTable({ resolved, targetName, avgMode = false }: Props) {
  const [colorMode, setColorMode] = useState<ColorMode>("yoy");
  // 注目指標 (サイドバー) の行を強調する (科別実績と同じ amber 枠)
  const focusedMetric = useFilters((s) => s.metric);

  // 月リスト (全 spec で共通の trendMonths を 1 つから取得)。
  // 直近12ヶ月平均モードはセルを12ヶ月移動平均にするので、 表示月 = 移動平均の月 (末尾13ヶ月)。
  const months = useMemo(() => {
    const t0 = resolved[0]?.trend ?? [];
    return (avgMode ? movingAvgTrend(t0, 12) : t0).map((p) => p.date);
  }, [resolved, avgMode]);

  return (
    <section className="card p-4">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h3 className="text-base font-semibold">{targetName} {resolved.length}指標 月別マトリックス</h3>
        <span className="text-[11px] text-slate-500">
          セル = {avgMode ? "12ヶ月移動平均" : "各月実績"} (13点) / 右側 = {avgMode ? "前年平均・予算平均" : "前年同月・選択月予算"}・対前年・対予算・達成度{avgMode ? "（直近12ヶ月平均）" : ""}
        </span>
        <div className="ml-auto inline-flex rounded border border-slate-300 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setColorMode("yoy")}
            className={`px-3 py-1 ${colorMode === "yoy"
              ? "bg-amber-500 text-white font-semibold"
              : "bg-white text-slate-600 hover:bg-slate-50"}`}
            title="セル色 = 当月÷前年同月 を 5 階調で表示"
          >前年比で色分け</button>
          <button
            type="button"
            onClick={() => setColorMode("budget")}
            className={`px-3 py-1 border-l border-slate-300 ${colorMode === "budget"
              ? "bg-amber-500 text-white font-semibold"
              : "bg-white text-slate-600 hover:bg-slate-50"}`}
            title="セル色 = 当月÷予算 を 5 階調で表示"
          >予算比で色分け</button>
        </div>
      </div>

      <div className="text-[10px] text-slate-500 mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">セル色 ({colorMode === "yoy" ? "対前年同月" : "対予算"}):</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-300 border border-slate-300"></span>+10% 以上</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-100 border border-slate-300"></span>+5% 以上</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-white border border-slate-300"></span>±5% 内</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-100 border border-slate-300"></span>-5% 以下</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-300 border border-slate-300"></span>-10% 以下</span>
        <span>・「減るほど良い」指標 (平均在院日数 / 薬剤材料費 等) は色が反転</span>
        <span className="ml-2 font-medium">達成度:</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block px-1 bg-blue-600 text-white rounded text-[9px] leading-tight">A</span>予算達成</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block px-1 bg-amber-400 text-white rounded text-[9px] leading-tight">B</span>前年超(予算未達)</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block px-1 bg-red-500 text-white rounded text-[9px] leading-tight">C</span>予算・前年とも未達</span>
      </div>

      <div className="overflow-x-auto text-xs">
        <table className="w-full border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr>
              <th className="text-left  px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20 w-52">指標</th>
              <th className="text-left  px-2 py-1 border-b border-slate-200 w-12">単位</th>
              {months.map((mo) => (
                <th key={mo} className="text-right px-1.5 py-1 border-b border-slate-200 tabular-nums">{monthLabel(mo)}</th>
              ))}
              <th className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">{avgMode ? "前年平均" : "前年同月"}</th>
              <th className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">{avgMode ? "予算平均" : "選択月予算"}</th>
              <th className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">対前年</th>
              <th className="text-right px-1.5 py-1 border-b border-slate-200 bg-slate-200">対予算</th>
              <th className="text-center px-1.5 py-1 border-b border-slate-200 bg-slate-200">達成度</th>
              <th className="text-left px-2 py-1 border-b border-slate-200 bg-slate-100">指標</th>
            </tr>
          </thead>
          <tbody>
            {resolved.map((r, ri) => (
              <Row key={r.spec.key} r={r} months={months} colorMode={colorMode}
                   avgMode={avgMode} focused={r.spec.key === focusedMetric} index={ri} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Row({ r, months, colorMode, avgMode, focused, index }: { r: ResolvedMetric; months: string[]; colorMode: ColorMode; avgMode: boolean; focused: boolean; index: number }) {
  const stripe = !focused && index % 2 === 1;  // ゼブラ (薄グレー/白 交互)
  const { spec } = r;
  const skipZero = SKIP_ZERO_KEYS.has(spec.key);
  const adj = (v: number | null): number | null => v == null ? null : (skipZero && v === 0 ? null : v);

  const isYen = isYenCardMetric(spec.key, spec.unit);   // 入院収入/粗利(GP) は 円(億/万) 表示

  // セル表示用 trend: 単月モード = 月次 raw / 直近12ヶ月平均モード = 12ヶ月移動平均 (末尾13点)。
  // skipZero 指標 (GP/単価/薬剤費) は予算未設定月の 0 を母数から除外 (右側 予算平均 列と整合)。
  const dispTrend = avgMode ? movingAvgTrend(r.trend, 12, skipZero) : r.trend;
  const byDate = new Map(dispTrend.map((p) => [p.date, p]));

  // 右側集計は raw trend の末尾12ヶ月平均 (= 移動平均の平均ではなく素の12ヶ月平均)。
  const last12 = r.trend.slice(-12);
  const meanT = (sel: (p: typeof r.trend[number]) => number | null | undefined): number | null => {
    const vals = last12.map((p) => adj(sel(p) ?? null)).filter((v): v is number => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const selMo = months[months.length - 1];
  const selP  = selMo ? byDate.get(selMo) : undefined;
  const sumA  = avgMode ? meanT((p) => p.actual)   : adj(selP?.actual   ?? null);
  const sumPY = avgMode ? meanT((p) => p.prevYear) : adj(selP?.prevYear ?? null);
  const sumB  = avgMode ? meanT((p) => p.budget)   : adj(selP?.budget   ?? null);
  const yoyRatio = (sumA != null && sumPY != null && sumPY !== 0) ? sumA / sumPY : null;
  const budRatio = (sumA != null && sumB  != null && sumB  !== 0) ? sumA / sumB  : null;
  const yoyBg = cellBg(yoyRatio != null ? yoyRatio - 1 : null, spec.direction);
  const budBg = cellBg(budRatio != null ? budRatio - 1 : null, spec.direction);
  const grade = gradeOf(sumA, sumB, sumPY, spec.direction);
  const baseLabel = avgMode ? "直近12ヶ月平均" : "選択月実績";

  // 入院収入/粗利(GP) の行は カードと同じ 円(億円/万円) 単位で表示 (2026-06-24)。
  //   億/万 は「期間内(trend)の最大実績」で判定 (カードも同じ基準 → 当月値で割れない、 ユーザ指示)。
  const repMaxK = isYen ? Math.max(0, ...r.trend.map((p) => Math.abs(p.actual ?? 0))) : 0;
  const useOku = isYen && yenUseOku(repMaxK);             // ≥1億円 → 億円、 それ未満 → 万円
  const rowUnit = isYen ? (useOku ? "億円" : "万円") : spec.unit;
  const fc = (v: number | null | undefined): string => {  // セル値 (単位なし)
    if (v == null) return "—";
    if (!isYen) return fmtByUnitValueOnly(v, spec.unit);
    const yen = v * 1000;
    return useOku ? (yen / 1e8).toFixed(2) : Math.round(yen / 1e4).toLocaleString();
  };
  const ft = (v: number | null | undefined): string =>    // tooltip 用 (単位付き)
    v == null ? "—" : (isYen ? `${fc(v)} ${rowUnit}` : fmtByUnitValueOnly(v, spec.unit));

  return (
    <tr className={focused ? "border-y-2 border-amber-500" : `border-b border-slate-100 hover:bg-blue-50/40 ${stripe ? "bg-slate-50" : ""}`}>
      <td className={`text-left px-2 py-0.5 font-medium sticky left-0 z-[1] ${focused ? "bg-white border-l-2 border-amber-500 font-bold" : stripe ? "bg-slate-50" : "bg-white"}`}>{spec.label}</td>
      <td className="text-left px-2 py-0.5 text-slate-500">{rowUnit}</td>
      {months.map((mo) => {
        const p = byDate.get(mo);
        const v   = adj(p?.actual   ?? null);
        const py  = adj(p?.prevYear ?? null);
        const bud = adj(p?.budget   ?? null);
        // 色は colorMode に応じて算出
        const refVal = colorMode === "yoy" ? py : bud;
        const pct = (v != null && refVal != null && refVal !== 0) ? (v - refVal) / Math.abs(refVal) : null;
        const bg = cellBg(pct, spec.direction);
        const ratioPct = (v != null && refVal != null && refVal !== 0) ? `${(v / refVal * 100).toFixed(1)}%` : "—";
        const refLabel = colorMode === "yoy" ? "前年" : "予算";
        const tooltip = v != null
          ? `${monthLabel(mo)}${avgMode ? " 時点の12ヶ月平均" : ""}\n実績: ${ft(v)}` +
            (py  != null ? `\n前年: ${ft(py)}` : "") +
            (bud != null ? `\n予算: ${ft(bud)}` : "") +
            (refVal != null ? `\n対${refLabel}: ${ratioPct}` : "")
          : "データなし";
        return (
          <td key={mo} className={`text-right px-1 py-0.5 tabular-nums ${bg}`} title={tooltip}>
            {v != null ? fc(v) : "—"}
          </td>
        );
      })}
      <td className={`text-right px-1.5 py-0.5 tabular-nums bg-slate-50 text-slate-600 ${focused ? "bg-amber-50" : ""}`}
          title={`${avgMode ? "前年平均" : "前年同月実績"}: ${ft(sumPY)}`}>
        {fc(sumPY)}
      </td>
      <td className={`text-right px-1.5 py-0.5 tabular-nums bg-slate-50 text-slate-600 ${focused ? "bg-amber-50" : ""}`}
          title={`${avgMode ? "予算平均" : "選択月予算"}: ${ft(sumB)}`}>
        {fc(sumB)}
      </td>
      <td className={`text-right px-1.5 py-0.5 tabular-nums font-semibold ${yoyBg}`}
          title={`対前年 = ${baseLabel} ÷ ${avgMode ? "前年平均" : "前年同月"}`}>
        {yoyRatio != null ? `${(yoyRatio * 100).toFixed(1)}%` : "—"}
      </td>
      <td className={`text-right px-1.5 py-0.5 tabular-nums font-semibold ${budBg}`}
          title={`対予算 = ${baseLabel} ÷ ${avgMode ? "予算平均" : "選択月予算"}`}>
        {budRatio != null ? `${(budRatio * 100).toFixed(1)}%` : "—"}
      </td>
      <td className={`text-center px-1.5 py-0.5 bg-slate-50 ${focused ? "border-r-2 border-amber-500" : ""}`}
          title="達成度: 予算達成=A / 予算未達でも前年超=B / それ以外=C">
        {grade
          ? <span className={`inline-flex w-5 h-5 rounded-full items-center justify-center text-[11px] font-extrabold ${gradeStyle(grade)}`}>{grade}</span>
          : <span className="text-slate-300">—</span>}
      </td>
      {/* 一番右にも 指標名 (横移動の負荷軽減) */}
      <td className={`text-left px-2 py-0.5 whitespace-nowrap ${focused ? "font-bold text-slate-800 border-r-2 border-amber-500" : "text-slate-600"}`}>{spec.label}</td>
    </tr>
  );
}

// 達成度 A/B/C (2026-06-16): 予算達成=A / 予算未達でも前年超=B / それ以外=C
// (lower_is_better は下回りが達成)。 評価できる基準が無ければ null。
function gradeOf(cur: number | null, budget: number | null, prevYear: number | null, direction: Direction): "A" | "B" | "C" | null {
  if (cur == null || direction === "neutral") return null;
  const beats = (ref: number | null): boolean | null =>
    ref == null ? null : (direction === "lower_is_better" ? cur <= ref : cur >= ref);
  const budOk = beats(budget), pyOk = beats(prevYear);
  if (budOk === true) return "A";
  if (pyOk === true) return "B";
  if (budOk == null && pyOk == null) return null;
  return "C";
}
function gradeStyle(g: "A" | "B" | "C"): string {
  return g === "A" ? "bg-blue-600 text-white" : g === "B" ? "bg-amber-400 text-white" : "bg-red-500 text-white";
}
